/* Elford Lane – theme helpers (carousels, tabs, countdown, cart, product page) */
let CLUE = null;
try { CLUE = (await import('@shopify/events')).CartLinesUpdateEvent; } catch (e) { CLUE = null; }

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

export function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  format = format || window.elMoneyFormat || '${{amount}}';
  const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
  const n = (num, precision, thousands = ',', decimal = '.') => {
    if (isNaN(num) || num == null) return '0';
    const parts = (num / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts.length > 1 && precision > 0 ? parts[0] + decimal + parts[1] : parts[0];
  };
  let value = '';
  switch (match ? match[1] : 'amount') {
    case 'amount_no_decimals': value = n(cents, 0); break;
    case 'amount_with_comma_separator': value = n(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': value = n(cents, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': value = n(cents, 2, "'", '.'); break;
    case 'amount_no_decimals_with_space_separator': value = n(cents, 0, ' ', ''); break;
    case 'amount_with_space_separator': value = n(cents, 2, ' ', ','); break;
    default: value = n(cents, 2);
  }
  return match ? format.replace(match[0], value) : value;
}

function toast(msg) {
  let t = document.querySelector('.el-toast');
  if (!t) { t = document.createElement('div'); t.className = 'el-toast'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
  t.textContent = msg; t.classList.add('is-visible');
  clearTimeout(t._h); t._h = setTimeout(() => t.classList.remove('is-visible'), 3500);
}

/* Adds items to the cart and lets the Horizon cart drawer/count refresh itself. */
export async function addToCart(items, source = document) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
  let deferred = null;
  if (CLUE) {
    try {
      deferred = CLUE.createPromise();
      source.dispatchEvent(new CLUE({ action: 'add', context: 'product', lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })), promise: deferred.promise }));
    } catch (e) { deferred = null; }
  }
  const res = await fetch(root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') })
  });
  const data = await res.json();
  const cart = await fetch(root + 'cart.js').then((r) => r.json());
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);
  if (data.status) {
    if (deferred) deferred.resolve({ cart: CLUE.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'el-theme', itemCount } });
    throw new Error(data.description || data.message || 'Error');
  }
  if (deferred) {
    deferred.resolve({ cart: CLUE.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'el-theme', sourceId: 'el-theme', itemCount, sections: data.sections, didError: false } });
  } else {
    window.location.href = root + 'cart';
  }
  return cart;
}

/* ---------- Carousel ---------- */
function initCarousel(el) {
  if (el.dataset.elReady) return; el.dataset.elReady = '1';
  const track = el.querySelector('.el-track');
  if (!track) return;
  const prev = el.querySelector('.el-arrow--prev');
  const next = el.querySelector('.el-arrow--next');
  const dots = el.querySelector('.el-dots');
  const pageWidth = () => track.clientWidth;
  const update = () => {
    const max = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
    if (dots) {
      const pages = Math.max(1, Math.round(track.scrollWidth / pageWidth()));
      if (dots.children.length !== pages) {
        dots.innerHTML = '';
        for (let i = 0; i < pages; i++) {
          const b = document.createElement('button'); b.type = 'button'; b.setAttribute('aria-label', 'Slide ' + (i + 1));
          b.addEventListener('click', () => track.scrollTo({ left: i * pageWidth() }));
          dots.appendChild(b);
        }
      }
      const idx = Math.round(track.scrollLeft / pageWidth());
      [...dots.children].forEach((d, i) => d.classList.toggle('is-active', i === idx));
      dots.hidden = pages < 2;
    }
  };
  prev && prev.addEventListener('click', () => track.scrollBy({ left: -pageWidth() }));
  next && next.addEventListener('click', () => track.scrollBy({ left: pageWidth() }));
  track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  window.addEventListener('resize', update);
  update();
  const auto = parseInt(el.dataset.autoplay || '0', 10);
  if (auto > 0) {
    let paused = false;
    el.addEventListener('mouseenter', () => (paused = true));
    el.addEventListener('mouseleave', () => (paused = false));
    setInterval(() => {
      if (paused || document.hidden) return;
      const max = track.scrollWidth - track.clientWidth - 2;
      track.scrollTo({ left: track.scrollLeft >= max ? 0 : track.scrollLeft + pageWidth() });
    }, auto * 1000);
  }
}

/* ---------- Tabs ---------- */
function initTabs(el) {
  if (el.dataset.elReady) return; el.dataset.elReady = '1';
  const tabs = [...el.querySelectorAll('[data-el-tab]')];
  const panels = [...el.querySelectorAll('[data-el-panel]')];
  const views = [...el.querySelectorAll('[data-el-viewall]')];
  const select = (id) => {
    tabs.forEach((t) => { const on = t.dataset.elTab === id; t.classList.toggle('is-active', on); t.setAttribute('aria-selected', on); });
    panels.forEach((p) => { p.hidden = p.dataset.elPanel !== id; });
    views.forEach((v) => { v.hidden = v.dataset.elViewall !== id; });
    const p = panels.find((p) => !p.hidden); p && p.querySelectorAll('.el-carousel').forEach((c) => c.querySelector('.el-track')?.dispatchEvent(new Event('scroll')));
  };
  tabs.forEach((t) => t.addEventListener('click', () => select(t.dataset.elTab)));
  el.addEventListener('shopify:block:select', (e) => { const id = e.target.dataset.elPanel || e.target.dataset.elTab; id && select(id); });
}

/* ---------- Countdown ---------- */
function initCountdown(el) {
  if (el.dataset.elReady) return; el.dataset.elReady = '1';
  const mode = el.dataset.mode;
  const getEnd = () => {
    if (mode === 'daily') { const d = new Date(); d.setHours(23, 59, 59, 999); return d.getTime(); }
    const t = Date.parse(el.dataset.end);
    return isNaN(t) ? 0 : t;
  };
  const parts = { d: el.querySelector('[data-d]'), h: el.querySelector('[data-h]'), m: el.querySelector('[data-m]'), s: el.querySelector('[data-s]') };
  const pad = (n) => String(n).padStart(2, '0');
  const tick = () => {
    let diff = Math.max(0, getEnd() - Date.now());
    if (diff === 0 && el.dataset.hideExpired === 'true' && mode !== 'daily') { const s = el.closest('.el-countdown-wrap'); if (s) s.hidden = true; }
    const d = Math.floor(diff / 864e5); diff -= d * 864e5;
    const h = Math.floor(diff / 36e5); diff -= h * 36e5;
    const m = Math.floor(diff / 6e4); diff -= m * 6e4;
    const s = Math.floor(diff / 1e3);
    parts.d && (parts.d.textContent = pad(d)); parts.h && (parts.h.textContent = pad(h)); parts.m && (parts.m.textContent = pad(m)); parts.s && (parts.s.textContent = pad(s));
  };
  tick(); setInterval(tick, 1000);
}

/* ---------- Quick add buttons (cards / upsells) ---------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-el-add]');
  if (!btn) return;
  e.preventDefault();
  if (btn.classList.contains('is-loading')) return;
  btn.classList.add('is-loading');
  try {
    await addToCart([{ id: Number(btn.dataset.elAdd), quantity: 1 }], btn);
  } catch (err) { toast(err.message); }
  btn.classList.remove('is-loading');
});

/* ---------- Product page ---------- */
class ElProduct {
  constructor(el) {
    this.el = el;
    const dataEl = el.querySelector('[data-el-product-json]');
    if (!dataEl) return;
    this.data = JSON.parse(dataEl.textContent);
    this.form = el.querySelector('[data-el-form]');
    this.idInput = el.querySelector('[data-el-variant-id]');
    this.strings = this.data.strings || {};
    this.current = this.data.variants.find((v) => v.id === this.data.selected) || this.data.variants[0];
    el.addEventListener('change', (e) => { if (e.target.matches('[data-el-option]')) this.onOptionChange(); });
    this.initQty();
    this.initGallery();
    this.initSticky();
    this.initDialogs();
    this.form && this.form.addEventListener('submit', (e) => this.onSubmit(e));
    this.render(false);
  }
  selectedOptions() {
    return this.data.options.map((_, i) => { const c = this.el.querySelector(`[data-el-option][data-index="${i}"]:checked`); return c ? c.value : null; });
  }
  onOptionChange() {
    const opts = this.selectedOptions();
    this.current = this.data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    this.render(true);
  }
  render(fromUser) {
    const v = this.current;
    const opts = this.selectedOptions();
    // option labels + availability
    this.data.options.forEach((name, i) => {
      const lbl = this.el.querySelector(`[data-el-option-value="${i}"]`);
      if (lbl) lbl.textContent = opts[i] || '';
      this.el.querySelectorAll(`[data-el-option][data-index="${i}"]`).forEach((input) => {
        const test = opts.slice(); test[i] = input.value;
        const ok = this.data.variants.some((vv) => vv.available && vv.options.every((o, j) => (j === i || j < i || true) && o === test[j]));
        input.closest('.el-opt')?.classList.toggle('is-unavailable', !ok);
      });
    });
    if (this.idInput) this.idInput.value = v ? v.id : '';
    // price
    this.el.querySelectorAll('[data-el-price]').forEach((p) => {
      if (!v) return;
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      p.innerHTML = `<span class="${onSale ? 'el-price__sale' : 'el-price__regular'}">${formatMoney(v.price)}</span>` + (onSale ? `<span class="el-price__compare">${formatMoney(v.compare_at_price)}</span>` : '') + (onSale && this.data.showSaveBadge ? `<span class="el-badge">${(this.strings.save || 'Save [percent]%').replace('[percent]', Math.round((1 - v.price / v.compare_at_price) * 100))}</span>` : '');
    });
    // buttons
    const label = !v ? this.strings.unavailable : v.available ? this.strings.add : this.strings.soldout;
    this.el.querySelectorAll('[data-el-atc]').forEach((b) => { b.disabled = !v || !v.available; const t = b.querySelector('[data-el-atc-text]'); if (t) t.textContent = label; });
    // inventory
    const inv = this.el.querySelector('[data-el-inventory]');
    if (inv && v) {
      const th = parseInt(inv.dataset.threshold || '10', 10);
      let txt = '', cls = '';
      if (!v.available) { txt = inv.dataset.out; cls = 'is-out'; }
      else if (v.inventory_management && v.inventory_quantity > 0 && v.inventory_quantity <= th) { txt = inv.dataset.low.replace('[count]', v.inventory_quantity); cls = 'is-low'; }
      else if (inv.dataset.in) { txt = inv.dataset.in; cls = 'is-in'; }
      inv.querySelector('[data-el-inventory-text]').textContent = txt; inv.className = 'el-inventory ' + cls; inv.hidden = !txt;
    }
    // size conversion
    const conv = this.el.querySelector('[data-el-conversion]');
    if (conv) {
      const idx = this.data.options.findIndex((o) => o.toLowerCase() === (conv.dataset.option || 'size').toLowerCase());
      const val = idx > -1 ? (opts[idx] || '') : '';
      const num = (val.match(/[\d.,]+/) || [''])[0];
      let found = false;
      conv.querySelectorAll('[data-row]').forEach((r) => { const on = r.dataset.row === val || (num && r.dataset.row === num); r.hidden = !on; if (on) found = true; });
      conv.hidden = !found;
    }
    // sku
    const sku = this.el.querySelector('[data-el-sku]'); if (sku && v) sku.textContent = v.sku || '';
    // url + media
    if (fromUser && v) {
      if (this.data.updateUrl) { const u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u.toString()); }
      if (v.media_id) this.goToMedia(v.media_id);
    }
  }
  initQty() {
    this.el.querySelectorAll('[data-el-qty]').forEach((wrap) => {
      const input = wrap.querySelector('input');
      wrap.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => {
        const step = b.dataset.step === 'minus' ? -1 : 1;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      }));
    });
  }
  async onSubmit(e) {
    e.preventDefault();
    const v = this.current; if (!v || !v.available) return;
    const qtyInput = this.el.querySelector('[data-el-qty] input');
    const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
    const buttons = [...this.el.querySelectorAll('[data-el-atc]')];
    buttons.forEach((b) => b.classList.add('is-loading'));
    const err = this.el.querySelector('[data-el-error]'); if (err) err.hidden = true;
    try { await addToCart([{ id: v.id, quantity: qty }], this.form); }
    catch (ex) { if (err) { err.textContent = ex.message; err.hidden = false; } else toast(ex.message); }
    buttons.forEach((b) => b.classList.remove('is-loading'));
  }
  initGallery() {
    const g = this.el.querySelector('[data-el-gallery]'); if (!g) return;
    this.gallery = g;
    const main = g.querySelector('.el-gallery__main');
    const thumbs = [...g.querySelectorAll('[data-el-thumb]')];
    const counter = g.querySelector('[data-el-counter]');
    const items = [...main.children];
    thumbs.forEach((t) => t.addEventListener('click', () => this.goToMedia(t.dataset.elThumb)));
    const setActive = (id) => {
      thumbs.forEach((t) => t.classList.toggle('is-active', t.dataset.elThumb === id));
      const i = items.findIndex((it) => it.dataset.mediaId === id);
      if (counter && i > -1) counter.textContent = `${i + 1} / ${items.length}`;
      g.querySelectorAll('.el-gallery__dots button').forEach((d, j) => d.classList.toggle('is-active', j === i));
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio > 0.55) setActive(en.target.dataset.mediaId); });
    }, { root: null, threshold: [0.55] });
    const ioMobile = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) setActive(en.target.dataset.mediaId); });
    }, { root: main, threshold: 0.6 });
    const mq = window.matchMedia('(max-width: 749px)');
    const bind = () => { io.disconnect(); ioMobile.disconnect(); items.forEach((it) => (mq.matches ? ioMobile : io).observe(it)); };
    mq.addEventListener('change', bind); bind();
    const dots = g.querySelector('.el-gallery__dots');
    if (dots) { items.forEach((it, i) => { const b = document.createElement('button'); b.type = 'button'; b.setAttribute('aria-label', 'Image ' + (i + 1)); b.addEventListener('click', () => this.goToMedia(it.dataset.mediaId)); dots.appendChild(b); }); }
    items[0] && setActive(items[0].dataset.mediaId);
  }
  goToMedia(id) {
    if (!this.gallery) return;
    const main = this.gallery.querySelector('.el-gallery__main');
    const target = main.querySelector(`[data-media-id="${id}"]`); if (!target) return;
    if (window.matchMedia('(max-width: 749px)').matches) main.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    else { const off = (document.body.style.getPropertyValue('--header-height') ? parseInt(document.body.style.getPropertyValue('--header-height'), 10) : 0) + 20; window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - off, behavior: 'smooth' }); }
  }
  initSticky() {
    const bar = document.querySelector(`[data-el-sticky="${this.el.dataset.section}"]`);
    const atc = this.el.querySelector('.el-buy [data-el-atc]');
    if (!bar || !atc) return;
    bar.querySelector('[data-el-sticky-btn]')?.addEventListener('click', () => {
      const needsChoice = this.data.options.length > 0 && this.data.variants.length > 1 && !this.data.preselected && !this.userTouched;
      if (needsChoice) { this.el.querySelector('.el-variants')?.scrollIntoView({ behavior: 'smooth', block: 'center' }); return; }
      this.form.requestSubmit ? this.form.requestSubmit() : this.form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
    this.el.addEventListener('change', () => (this.userTouched = true));
    const io = new IntersectionObserver(([en]) => {
      const past = !en.isIntersecting && en.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', past);
    });
    io.observe(atc);
  }
  initDialogs() {
    this.el.querySelectorAll('[data-el-open]').forEach((b) => b.addEventListener('click', () => {
      const d = document.getElementById(b.dataset.elOpen); if (d && d.showModal) { d.showModal(); document.documentElement.style.overflow = 'hidden'; }
    }));
    document.querySelectorAll('.el-drawer').forEach((d) => {
      if (d.dataset.elReady) return; d.dataset.elReady = '1';
      d.addEventListener('close', () => (document.documentElement.style.overflow = ''));
      d.addEventListener('click', (e) => { if (e.target === d || e.target.closest('[data-el-close]')) d.close(); });
    });
  }
}

/* ---------- Video tiles ---------- */
document.addEventListener('click', (e) => {
  const tile = e.target.closest('[data-el-video]');
  if (!tile) return;
  const v = tile.querySelector('video'); if (!v) return;
  if (v.paused) { v.play(); tile.classList.add('is-playing'); } else { v.pause(); tile.classList.remove('is-playing'); }
});

/* ---------- Init ---------- */
function init(scope = document) {
  scope.querySelectorAll('[data-el-carousel]').forEach(initCarousel);
  scope.querySelectorAll('[data-el-tabs]').forEach(initTabs);
  scope.querySelectorAll('[data-el-countdown]').forEach(initCountdown);
  scope.querySelectorAll('[data-el-product]').forEach((p) => { if (!p.dataset.elReady) { p.dataset.elReady = '1'; new ElProduct(p); } });
}
init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
window.ElfordLane = { addToCart, formatMoney, init };
